import { useCallback, useEffect, useRef, useState } from "react";
import {
  clearCreativeJobProgress,
  ensureCreativeJobProgressSession,
  getCreativeJobProgressState,
  migrateCreativeJobProgress,
  setCreativeJobProgress,
} from "../utils/creativeJobProgressStorage";
import {
  formatTaskEstimatedDuration,
  getTaskEstimatedDurationMs,
  getTaskRemainingDurationMs,
} from "../utils/taskEstimatedDurations";

/**
 * Persists loader progress + countdown for long-running creative/campaign tasks.
 *
 * @param {object} options
 * @param {string} options.storageKey Canonical persistence key (jobID or campaignID).
 * @param {string} [options.migrateFromKey] Temporary alert id to promote into storageKey.
 * @param {string} [options.jobType] Used to resolve estimated duration.
 * @param {boolean} options.isTerminal When true, clears persisted session.
 * @param {string|number} [options.runId] Identifies one attempt. A new id restarts progress from 0 on the same mounted alert.
 */
export const useCreativeJobProgressLoader = ({
  storageKey,
  migrateFromKey,
  jobType,
  isTerminal,
  itemCount,
  hasPrompt,
  runId,
}) => {
  const estimatedDurationMs = getTaskEstimatedDurationMs({
    jobType,
    itemCount,
    hasPrompt,
  });
  const hasMigratedRef = useRef(false);
  const runIdRef = useRef(runId);

  const [session, setSession] = useState(() => {
    const stored = getCreativeJobProgressState({
      jobID: storageKey,
      jobType,
      estimatedDurationMs,
    });
    // A remount initializes runIdRef to the current id, so the effect cannot
    // see the previous id. The stored start is older than this attempt.
    if (
      runId != null &&
      stored.startedAt &&
      Number(runId) > Number(stored.startedAt)
    ) {
      return {
        progress: 0,
        startedAt: null,
        estimatedDurationMs,
        remainingMs: estimatedDurationMs,
      };
    }
    return stored;
  });
  const [now, setNow] = useState(() => Date.now());
  const [trackedRunId, setTrackedRunId] = useState(runId);

  // Restart before paint. The loader only moves forward, so a later effect
  // that writes progress 0 cannot pull a bar already seeded at 99.
  if (runId != null && runId !== trackedRunId) {
    setTrackedRunId(runId);
    if (!isTerminal) {
      setSession({
        progress: 0,
        startedAt: null,
        estimatedDurationMs,
        remainingMs: estimatedDurationMs,
      });
      setNow(Date.now());
    }
  }

  useEffect(() => {
    if (
      !migrateFromKey ||
      !storageKey ||
      migrateFromKey === storageKey ||
      hasMigratedRef.current
    ) {
      return;
    }

    migrateCreativeJobProgress({
      fromKey: migrateFromKey,
      toKey: storageKey,
      jobType,
      estimatedDurationMs,
    });
    hasMigratedRef.current = true;
    setSession(
      getCreativeJobProgressState({
        jobID: storageKey,
        jobType,
        estimatedDurationMs,
      })
    );
  }, [migrateFromKey, storageKey, jobType, estimatedDurationMs]);

  useEffect(() => {
    if (!storageKey || isTerminal) return;

    const isNewRun = runId != null && runIdRef.current !== runId;
    const storedBefore = getCreativeJobProgressState({
      jobID: storageKey,
      jobType,
      estimatedDurationMs,
    });
    const isPreviousRun =
      runId != null &&
      storedBefore.startedAt &&
      Number(runId) > Number(storedBefore.startedAt);
    runIdRef.current = runId;

    if (isNewRun || isPreviousRun) {
      clearCreativeJobProgress(storageKey);
      if (migrateFromKey) clearCreativeJobProgress(migrateFromKey);
    }

    ensureCreativeJobProgressSession({
      jobID: storageKey,
      jobType,
      estimatedDurationMs,
    });

    setSession(
      getCreativeJobProgressState({
        jobID: storageKey,
        jobType,
        estimatedDurationMs,
      })
    );
  }, [
    storageKey,
    jobType,
    estimatedDurationMs,
    isTerminal,
    runId,
    migrateFromKey,
  ]);

  useEffect(() => {
    if (!storageKey || isTerminal) return;

    const intervalId = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(intervalId);
  }, [storageKey, isTerminal]);

  useEffect(() => {
    if (!isTerminal) return;

    if (storageKey) clearCreativeJobProgress(storageKey);
    if (migrateFromKey) clearCreativeJobProgress(migrateFromKey);
  }, [isTerminal, storageKey, migrateFromKey]);

  const onProgressChange = useCallback(
    (value) => {
      if (!storageKey) return;

      const nextProgress = Math.min(99, Math.max(0, Number(value) || 0));

      setCreativeJobProgress({
        jobID: storageKey,
        progress: nextProgress,
        jobType,
        estimatedDurationMs,
      });

      // Bump local progress only — do not re-read storage with Date.now().
      // Recomputing elapsed on every tick feeds a new seedProgress float back
      // into LinearLoaderWithTime and causes Maximum update depth exceeded.
      setSession((prev) => {
        if (nextProgress <= (Number(prev.progress) || 0)) {
          return prev;
        }
        return {
          ...prev,
          progress: nextProgress,
        };
      });
    },
    [storageKey, jobType, estimatedDurationMs]
  );

  const sessionDurationMs = session.estimatedDurationMs || estimatedDurationMs;

  const remainingMs = getTaskRemainingDurationMs({
    startedAt: session.startedAt,
    estimatedDurationMs: sessionDurationMs,
    now,
  });

  let seedProgress = Math.min(99, Math.max(0, Number(session.progress) || 0));
  if (session.startedAt && sessionDurationMs > 0) {
    const elapsedProgress =
      ((now - session.startedAt) / sessionDurationMs) * 100;
    seedProgress = Math.min(99, Math.max(seedProgress, elapsedProgress));
  }

  return {
    loaderTimeMs: estimatedDurationMs,
    onProgressChange,
    seedProgress,
    storageKey,
    remainingMs,
    formattedRemainingTime: formatTaskEstimatedDuration({ remainingMs }),
    startedAt: session.startedAt,
    estimatedDurationMs: sessionDurationMs,
  };
};
